import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  CustomerDetailResponse,
  CustomersListResponse,
  CustomerWriteInput,
  CustomerWriteResponse,
  ImportResponse,
} from './contractsMirror.js';
import type { ClientsSearch } from './searchParams.js';

/**
 * TanStack Query hooks for the Clients (customers) endpoints. The request goes
 * through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh); the tenant is derived
 * server-side from the auth context and the SPA NEVER sends a tenant id (SEC-11).
 *
 * The list search is SERVER-SIDE: `?q` is passed to the endpoint, one query key
 * per search text, so typing in the box refetches exactly one query. The page
 * size is a constant (the directory is a single screen, not an infinite scroll).
 */

/** Page size sent to the coarse list endpoint. */
export const CLIENTS_PAGE_SIZE = 100;

/** Build the stable query key for a list filter (one cache entry per search text). */
export function clientsQueryKey(search: ClientsSearch): readonly unknown[] {
  return ['clients', search.q ?? null] as const;
}

/** Build the coarse-endpoint request path with the active filters (no tenant param). */
export function clientsRequestPath(search: ClientsSearch): string {
  const params = new URLSearchParams();
  if (search.q) params.set('q', search.q);
  params.set('page', '1');
  params.set('pageSize', String(CLIENTS_PAGE_SIZE));
  return `/api/v1/customers?${params.toString()}`;
}

export function useClientsQuery(search: ClientsSearch) {
  return useQuery<CustomersListResponse>({
    queryKey: clientsQueryKey(search),
    queryFn: () => apiFetchJson<CustomersListResponse>(clientsRequestPath(search)),
  });
}

export function clientQueryKey(id: number): readonly unknown[] {
  return ['client', id] as const;
}

export function useClientQuery(id: number, enabled = true) {
  return useQuery<CustomerDetailResponse>({
    queryKey: clientQueryKey(id),
    queryFn: () => apiFetchJson<CustomerDetailResponse>(`/api/v1/customers/${id}`),
    enabled: enabled && Number.isFinite(id) && id > 0,
  });
}

/**
 * Client write mutations: create (POST /api/v1/customers), update
 * (PUT /api/v1/customers/:id — carries `portal_enabled?` for the client page's
 * portal switch), import (POST /api/v1/customers/import — the document scan), and
 * merge (POST /api/v1/customers/:loserId/merge with `{ into: winnerId }`).
 * On success each invalidates the list + detail caches so every surface re-syncs
 * to the authoritative server state.
 */
function invalidateClients(queryClient: ReturnType<typeof useQueryClient>, clientId?: number) {
  void queryClient.invalidateQueries({ queryKey: ['clients'] });
  if (clientId) void queryClient.invalidateQueries({ queryKey: clientQueryKey(clientId) });
}

/** Create a new client (POST /api/v1/customers). */
export function useCreateClientMutation() {
  const queryClient = useQueryClient();
  return useMutation<CustomerWriteResponse, Error, CustomerWriteInput>({
    mutationFn: (input) =>
      apiFetchJson<CustomerWriteResponse>('/api/v1/customers', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateClients(queryClient),
  });
}

/** Update a client (PUT /api/v1/customers/:id). The portal toggle rides this. */
export function useUpdateClientMutation(clientId: number) {
  const queryClient = useQueryClient();
  return useMutation<CustomerWriteResponse, Error, CustomerWriteInput>({
    mutationFn: (input) =>
      apiFetchJson<CustomerWriteResponse>(`/api/v1/customers/${clientId}`, {
        method: 'PUT',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => invalidateClients(queryClient, clientId),
  });
}

/**
 * Scan documents for clients (POST /api/v1/customers/import) — creates clients
 * from customer names found on estimates/invoices, links the documents, and
 * returns the suspected-duplicate groups for the merge screen.
 */
export function useImportClientsMutation() {
  const queryClient = useQueryClient();
  return useMutation<ImportResponse, Error, void>({
    mutationFn: () => apiFetchJson<ImportResponse>('/api/v1/customers/import', { method: 'POST' }),
    onSuccess: () => invalidateClients(queryClient),
  });
}

/** Merge one client into another (POST /api/v1/customers/:loserId/merge). */
export function useMergeClientMutation() {
  const queryClient = useQueryClient();
  return useMutation<CustomerWriteResponse, Error, { loserId: number; intoId: number }>({
    mutationFn: ({ loserId, intoId }) =>
      apiFetchJson<CustomerWriteResponse>(`/api/v1/customers/${loserId}/merge`, {
        method: 'POST',
        body: JSON.stringify({ into: intoId }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (_data, { loserId, intoId }) => {
      invalidateClients(queryClient, intoId);
      // The loser no longer exists — drop its cache entry rather than invalidate it.
      queryClient.removeQueries({ queryKey: clientQueryKey(loserId) });
    },
  });
}
